import Vue from 'vue';
import { Module } from 'vuex';

import { RootState, CourseEditLesson } from 'frontend/types';
import { CourseLessonSearchInput } from 'frontend/types/authoring/requests';
import { apiResolve } from 'frontend/router';
import { apiRequestWithErrors } from 'frontend/store/modules/lessons/apiHelpers';

interface CourseLessonSearchState {
  lesson?: CourseEditLesson;
  lessonSearchError: string;
  waitingOnRequest: boolean;
}

/** A slightly more concise way to create a new CourseLessonSearchState object */
function createStateObj(newState: CourseLessonSearchState): CourseLessonSearchState {
  return newState;
}

const courseLessonSearch: Module<CourseLessonSearchState, RootState> = {
  namespaced: true,

  state: {
    lesson: undefined,
    lessonSearchError: '',
    waitingOnRequest: false,
  },

  mutations: {
    setValue(state, value: CourseLessonSearchState): void {
      // Something about this changing from undefined to some-kind-of-object requires using Vue.set
      Vue.set(state, 'lesson', value.lesson);

      state.lessonSearchError = value.lessonSearchError;
      state.waitingOnRequest = value.waitingOnRequest;
    },
  },

  actions: {
    async search(
      { commit },
      { lesson_slug, all_course_lesson_slugs }: CourseLessonSearchInput,
    ): Promise<void> {
      commit(
        'setValue',
        createStateObj({
          lesson: undefined,
          lessonSearchError: '',
          waitingOnRequest: true,
        }),
      );

      if (all_course_lesson_slugs.includes(lesson_slug)) {
        commit(
          'setValue',
          createStateObj({
            lesson: undefined,
            lessonSearchError: 'That lesson is already included in this course.',
            waitingOnRequest: false,
          }),
        );
        return;
      }

      const { href } = apiResolve({ name: 'course_lesson_search' });
      const [_, result, errors] = await apiRequestWithErrors(href, { lesson_slug });

      const searchResult = result?.course_lesson_search;

      commit(
        'setValue',
        createStateObj({
          lesson: searchResult?.lesson,
          lessonSearchError: errors.length > 0 ? errors[0] : searchResult?.error || '',
          waitingOnRequest: false,
        }),
      );
    },

    clear({ commit }): void {
      commit(
        'setValue',
        createStateObj({
          lesson: undefined,
          lessonSearchError: '',
          waitingOnRequest: false,
        }),
      );
    },
  },

  getters: {
    lessonSearchWaitingOnRequest(state): boolean {
      return state.waitingOnRequest;
    },
  },
};

export default courseLessonSearch;
